import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AlertModal, ConfirmModal } from './AlertModal';
import { useAuth } from '../context/AuthContext';
import { backdropVariants, modalVariants } from '../animations';
import GlowCard from './GlowCard';
import './admins/admins.css';

/* ── Role colour (admins.css tokens) ───────────────────────────── */
const ROLE_META = {
    super: { tone: 'var(--ad-super)' },
    panel: { tone: 'var(--ad-panel)' },
    sub: { tone: 'var(--ad-sub)' },
};
const roleKeyOf = (admin) => ((admin.role === 'super_admin' || admin.role === 'admin') ? 'super' : admin.created_by_admin_id ? 'sub' : 'panel');

/** The role as a coloured word (was a tinted pill with an icon). */
const RoleBadge = ({ role, createdByAdminId }) => {
    const { t } = useTranslation();
    const key = roleKeyOf({ role, created_by_admin_id: createdByAdminId });
    const label = key === 'super' ? t('admins.superAdmin', 'Super Admin') : key === 'sub' ? t('admins.subAdmin', 'Sub Admin') : t('admins.panelAdmin', 'Panel Admin');
    return <span className="adm-role" style={{ '--ad-tone': ROLE_META[key].tone }}>{label}</span>;
};

/* ── Reset-Password Modal ───────────────────────────────────────── */
const ResetPasswordModal = ({ show, admin, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) { setNewPassword(''); setError(''); }
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
        setLoading(true);
        setError('');
        try {
            const sourceParam = admin.source ? `?source=${admin.source}` : '';
            await axios.put(`/api/admins/${admin.id}/reset-password${sourceParam}`, { newPassword });
            onSuccess(`Password reset successfully for "${admin.username}"`);
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {show && admin && (
                <motion.div
                    variants={backdropVariants} initial="hidden" animate="visible" exit="hidden"
                    onClick={onClose} className="adm-scrim top"
                >
                    <motion.div
                        variants={modalVariants} initial="hidden" animate="visible" exit="exit"
                        onClick={e => e.stopPropagation()} className="adm-dialog narrow" role="dialog" aria-modal="true"
                    >
                        <div className="adm-dialog-head">
                            <div>
                                <h3>{t('admins.resetPassword', 'Reset Password')}</h3>
                                <p>{t('admins.setNewPasswordFor', 'Set a new password for')} <strong>{admin.username}</strong></p>
                            </div>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="adm-dialog-body one">
                                <input
                                    type="password" className="adm-input"
                                    placeholder={t('admins.newPasswordPlaceholder', 'New password (min 6 characters)')}
                                    aria-label={t('admins.newPasswordPlaceholder', 'New password (min 6 characters)')}
                                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                    autoFocus
                                />
                                {error && <p className="adm-error">{error}</p>}
                            </div>
                            <div className="adm-dialog-foot">
                                <button type="button" onClick={onClose} className="adm-btn">{t('common.cancel', 'Cancel')}</button>
                                <button type="submit" disabled={loading} className="adm-btn primary">
                                    {loading ? t('admins.resetting', 'Resetting…') : t('admins.resetPassword', 'Reset Password')}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* ── Admin Row Card ─────────────────────────────────────────────── */
const AdminRow = ({ admin, onDelete, onRoleChange, onResetPassword, showAddedBy, isSuperAdmin, currentUser }) => {
    const { t } = useTranslation();
    const isSelf = currentUser && admin.source === currentUser.source && Number(admin.id) === Number(currentUser.id);
    const displayName = admin.name || admin.username || t('admins.unnamed', 'Unnamed');
    const initial = (displayName || '?')[0].toUpperCase();
    const isSuper = admin.role === 'super_admin' || admin.role === 'admin';

    return (
        <div className="adm-row" style={{ '--ad-tone': ROLE_META[roleKeyOf(admin)].tone }}>
            <div className="adm-avatar" aria-hidden="true">{initial}</div>

            <div className="adm-who">
                <div className="adm-name-line">
                    <span className="adm-name">{displayName}</span>
                    <RoleBadge role={admin.role} createdByAdminId={admin.created_by_admin_id} />
                    {isSelf && <span className="adm-you">({t('admins.you', 'You')})</span>}
                </div>
                <div className="adm-meta">
                    {admin.username && <span className="user">@{admin.username}</span>}
                    {admin.steam_id ? (
                        <a href={`https://steamcommunity.com/profiles/${admin.steam_id}`} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()} title={t('admins.openSteamProfile', 'Open Steam profile')}>{admin.steam_id}</a>
                    ) : <span>—</span>}
                    {showAddedBy && admin.created_by_admin_name && (
                        <span>{t('admins.addedBy', 'Added by')}: <b>{admin.created_by_admin_name}</b></span>
                    )}
                </div>
            </div>

            <div className="adm-actions">
                {isSuperAdmin && !isSelf && !admin.created_by_admin_id && admin.role === 'panel_admin' && (
                    <button type="button" className="adm-btn small good" onClick={() => onRoleChange(admin, 'super_admin')} title={t('admins.promoteToSuper', 'Promote to Super Admin')}>
                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /></svg>
                        {t('admins.promote', 'Promote')}
                    </button>
                )}
                {isSuperAdmin && !isSelf && !admin.created_by_admin_id && isSuper && (
                    <button type="button" className="adm-btn small warn" onClick={() => onRoleChange(admin, 'panel_admin')} title={t('admins.demoteToPanel', 'Demote to Panel Admin')}>
                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                        {t('admins.demote', 'Demote')}
                    </button>
                )}
                {isSuperAdmin && !isSelf && (
                    <button type="button" className="adm-btn small" onClick={() => onResetPassword(admin)} title={t('admins.resetPassword', 'Reset Password')}>
                        {t('admins.resetPwd', 'Reset Pwd')}
                    </button>
                )}
                {!isSelf && (
                    <button type="button" className="adm-btn small danger" onClick={() => onDelete(admin)}>{t('common.remove', 'Remove')}</button>
                )}
            </div>
        </div>
    );
};

/* ── Admin Group Section ────────────────────────────────────────── */
const AdminGroup = ({ admins, title, roleKey, emptyText, ...rowProps }) => {
    const meta = ROLE_META[roleKey];
    return (
        <section>
            <div className="adm-group-head">
                <h2>{title}</h2>
                <span className="adm-count" style={{ '--ad-tone': meta.tone }}>{admins.length}</span>
            </div>
            {admins.length === 0 ? (
                <div className="adm-empty">{emptyText}</div>
            ) : (
                <div className="adm-list">
                    {admins.map(admin => (
                        <AdminRow key={`${admin.source || 'iga'}-${admin.id}`} admin={admin} {...rowProps} />
                    ))}
                </div>
            )}
        </section>
    );
};

const Admins = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [newAdmin, setNewAdmin] = useState({ steam_id: '', name: '', username: '', password: '', role: 'panel_admin' });
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const [resetPwdModal, setResetPwdModal] = useState({ show: false, admin: null });

    const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
    const isPanelAdmin = user?.role === 'panel_admin';

    useEffect(() => { fetchAdmins(); }, []);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admins');
            setAdmins(response.data);
            setLoading(false);
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Failed to load admins.';
            setAlertModal({ show: true, message: errorMsg, type: 'error' });
            setLoading(false);
        }
    };

    const handleAddAdmin = async (e) => {
        e.preventDefault();
        if (!newAdmin.steam_id || !newAdmin.username || !newAdmin.password) {
            setAlertModal({ show: true, message: 'Steam ID, Username, and Password are required', type: 'error' });
            return;
        }
        try {
            await axios.post('/api/admins', newAdmin);
            setAlertModal({ show: true, message: isPanelAdmin ? 'Sub-admin added successfully' : 'Admin added successfully', type: 'success' });
            setNewAdmin({ steam_id: '', name: '', username: '', password: '', role: 'panel_admin' });
            setShowAddModal(false);
            fetchAdmins();
        } catch (err) {
            setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to add admin', type: 'error' });
        }
    };

    const handleDeleteAdmin = (admin) => {
        setConfirmModal({
            show: true,
            message: admin.created_by_admin_id
                ? t('admins.removeSubConfirm', 'Remove sub-admin "{{name}}"? This cannot be undone.', { name: admin.username || admin.name })
                : t('admins.removeAdminConfirm', 'Remove admin "{{name}}"? This cannot be undone.', { name: admin.username || admin.name }),
            onConfirm: async () => {
                setConfirmModal({ show: false, message: '', onConfirm: null });
                try {
                    const sourceParam = admin.source ? `?source=${admin.source}` : '';
                    await axios.delete(`/api/admins/${admin.id}${sourceParam}`);
                    setAlertModal({ show: true, message: 'Admin removed', type: 'success' });
                    fetchAdmins();
                } catch (err) {
                    setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to remove admin', type: 'error' });
                }
            }
        });
    };

    const handleRoleChange = (admin, newRole) => {
        const roleLabel = newRole === 'super_admin' ? t('admins.superAdmin', 'Super Admin') : t('admins.panelAdmin', 'Panel Admin');
        setConfirmModal({
            show: true,
            message: newRole === 'super_admin'
                ? t('admins.promoteConfirm', 'Promote "{{name}}" to {{role}}?', { name: admin.username || admin.name, role: roleLabel })
                : t('admins.demoteConfirm', 'Demote "{{name}}" to {{role}}?', { name: admin.username || admin.name, role: roleLabel }),
            onConfirm: async () => {
                setConfirmModal({ show: false, message: '', onConfirm: null });
                try {
                    const sourceParam = admin.source ? `?source=${admin.source}` : '';
                    await axios.put(`/api/admins/${admin.id}/role${sourceParam}`, { role: newRole });
                    setAlertModal({ show: true, message: `${admin.username} is now ${roleLabel}`, type: 'success' });
                    fetchAdmins();
                } catch (err) {
                    setAlertModal({ show: true, message: err.response?.data?.error || 'Failed to update role', type: 'error' });
                }
            }
        });
    };

    const handleResetPassword = (admin) => setResetPwdModal({ show: true, admin });

    const superAdmins = admins.filter(a => a.role === 'super_admin' || a.role === 'admin');
    const panelAdmins = admins.filter(a => (a.role === 'panel_admin') && !a.created_by_admin_id);
    const subAdmins = admins.filter(a => a.role === 'panel_admin' && a.created_by_admin_id);

    const rowProps = { onDelete: handleDeleteAdmin, onRoleChange: handleRoleChange, onResetPassword: handleResetPassword, isSuperAdmin, currentUser: user };

    return (
        <div className="adm-page">
            {/* Header */}
            <div className="adm-head">
                <div>
                    <h1 className="adm-title">{isPanelAdmin ? t('admins.subAdminManagement', 'Sub-Admin Management') : t('admins.adminManagement', 'Admin Management')}</h1>
                    <p className="adm-sub">
                        {isPanelAdmin ? t('admins.subAdminSubtitle', 'Manage the sub-admins under your account') : t('admins.adminSubtitle', 'Manage panel administrators and their permissions')}
                    </p>
                </div>
                <button type="button" onClick={() => setShowAddModal(true)} className="adm-btn primary">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    {isPanelAdmin ? t('admins.addSubAdmin', 'Add Sub-Admin') : t('admins.addAdmin', 'Add Admin')}
                </button>
            </div>

            {loading ? (
                <div className="adm-list">
                    {[0, 1, 2].map(i => <div key={i} className="adm-skel" />)}
                </div>
            ) : isSuperAdmin ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <AdminGroup admins={superAdmins} roleKey="super" title={t('admins.superAdmins', 'Super Admins')} emptyText={t('admins.noSuperAdmins', 'No super admins found.')} showAddedBy={false} {...rowProps} />
                    <AdminGroup admins={panelAdmins} roleKey="panel" title={t('admins.panelAdmins', 'Panel Admins')} emptyText={t('admins.noPanelAdmins', 'No panel admins added yet.')} showAddedBy={false} {...rowProps} />
                    <AdminGroup admins={subAdmins} roleKey="sub" title={t('admins.subAdmins', 'Sub Admins')} emptyText={t('admins.noSubAdminsByPanel', 'No sub-admins have been added by panel admins yet.')} showAddedBy={true} {...rowProps} />
                </div>
            ) : (
                <AdminGroup admins={admins} roleKey="sub" title={t('admins.yourSubAdmins', 'Your Sub-Admins')} emptyText={t('admins.noSubAdminsYet', 'No sub-admins added yet. Use the button above to add your first sub-admin.')} showAddedBy={false} {...rowProps} />
            )}

            {/* Add Admin Modal */}
            <AnimatePresence>
                {showAddModal && (
                    <motion.div
                        variants={backdropVariants} initial="hidden" animate="visible" exit="hidden"
                        onClick={() => setShowAddModal(false)} className="adm-scrim"
                    >
                        <motion.div
                            variants={modalVariants} initial="hidden" animate="visible" exit="exit"
                            onClick={e => e.stopPropagation()} className="modal-container adm-dialog" role="dialog" aria-modal="true"
                        >
                            <div className="adm-dialog-head">
                                <h2>{isPanelAdmin ? t('admins.addSubAdmin', 'Add Sub-Admin') : t('admins.addAdministrator', 'Add Administrator')}</h2>
                                <button type="button" className="adm-x" onClick={() => setShowAddModal(false)} aria-label={t('common.close', 'Close')}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </button>
                            </div>
                            <form onSubmit={handleAddAdmin}>
                                <div className="adm-dialog-body">
                                    <div className="adm-field">
                                        <label htmlFor="ad-steam">{t('admins.steamId64', 'Steam ID (Steam64)')}</label>
                                        <input id="ad-steam" type="text" className="adm-input mono" value={newAdmin.steam_id} onChange={(e) => setNewAdmin({ ...newAdmin, steam_id: e.target.value })} placeholder="76561198xxxxxxxx" autoFocus />
                                    </div>
                                    <div className="adm-field">
                                        <label htmlFor="ad-name">{t('admins.nameOptional', 'Name (Optional)')}</label>
                                        <input id="ad-name" type="text" className="adm-input" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder={t('admins.adminNamePlaceholder', 'Admin Name')} />
                                    </div>
                                    <div className="adm-field">
                                        <label htmlFor="ad-user">{t('admins.usernameForLogin', 'Username (for panel login)')}</label>
                                        <input id="ad-user" type="text" className="adm-input" value={newAdmin.username} onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })} placeholder="username" required />
                                    </div>
                                    <div className="adm-field">
                                        <label htmlFor="ad-pass">{t('admins.passwordForLogin', 'Password (for panel login)')}</label>
                                        <input id="ad-pass" type="password" className="adm-input" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder="••••••••" required />
                                    </div>
                                    {isSuperAdmin && (
                                        <div className="adm-field full">
                                            <label htmlFor="ad-role">{t('admins.role', 'Role')}</label>
                                            <select id="ad-role" className="adm-input" value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}>
                                                <option value="panel_admin">{t('admins.panelAdmin', 'Panel Admin')}</option>
                                                <option value="super_admin">{t('admins.superAdmin', 'Super Admin')}</option>
                                            </select>
                                        </div>
                                    )}
                                    <div className="adm-note full">
                                        {isPanelAdmin ? (
                                            <p><strong>{t('admins.subAdminLabel', 'Sub-Admin:')}</strong> {t('admins.subAdminHelp', 'Users you add here can manage your servers, matches, and teams. They cannot add more sub-admins.')}</p>
                                        ) : (
                                            <p><strong style={{ '--ad-tone': ROLE_META.super.tone }}>{t('admins.superAdmin', 'Super Admin')}</strong> - {t('admins.superAdminHelp', 'Full access including admin management.')} <strong style={{ '--ad-tone': ROLE_META.panel.tone }}>{t('admins.panelAdmin', 'Panel Admin')}</strong> - {t('admins.panelAdminHelp', 'Can manage servers, matches, teams, and add their own sub-admins.')}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="adm-dialog-foot">
                                    <button type="button" onClick={() => setShowAddModal(false)} className="adm-btn">{t('common.cancel', 'Cancel')}</button>
                                    <button type="submit" className="adm-btn primary">{isPanelAdmin ? t('admins.addSubAdmin', 'Add Sub-Admin') : t('admins.addAdmin', 'Add Admin')}</button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AlertModal show={alertModal.show} message={alertModal.message} type={alertModal.type} onClose={() => setAlertModal({ ...alertModal, show: false })} />
            <ConfirmModal show={confirmModal.show} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onClose={() => setConfirmModal({ ...confirmModal, show: false })} />
            <ResetPasswordModal
                show={resetPwdModal.show}
                admin={resetPwdModal.admin}
                onClose={() => setResetPwdModal({ show: false, admin: null })}
                onSuccess={(msg) => setAlertModal({ show: true, message: msg, type: 'success' })}
            />
        </div>
    );
};

export default Admins;
